<template>
  <form class="vl-form" @submit.prevent="onSubmit()">
    <vl-grid v-if="definition" v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-properties-list v-if="definition">
          <vl-properties-label>{{ $t('deliveryprocessdefinition.detail.id') }}</vl-properties-label>
          <vl-properties-data>{{ definition.id }}</vl-properties-data>
          <vl-properties-label>{{ $t('deliveryprocessdefinition.detail.label') }}</vl-properties-label>
          <vl-properties-data>{{ definition.label }}</vl-properties-data>
          <vl-properties-label>{{ $t('deliveryprocessdefinition.detail.organisation') }}</vl-properties-label>
          <vl-properties-data>{{ definition.organisation }} ({{ definition.ovoNumber }})</vl-properties-data>
        </vl-properties-list>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-form-column v-if="hasParent" v-vl-spacer:top.small>
          <PbsRadioButtonField name="importData" :label="$t('deliveryprocessdefinition.actions.dryrun.importData')" />
        </vl-form-column>
        <vl-form-column v-vl-spacer:top.small>
          <PbsRadioButtonField
            name="includeProductProcessDefinitions"
            :label="$t('deliveryprocessdefinition.actions.dryrun.includeProductProcessDefinitions')"
          />
        </vl-form-column>
        <vl-form-column v-vl-spacer:top.small>
          <PbsRadioButtonField
            name="includeTriggeredDeliveryDefinitions"
            :label="$t('deliveryprocessdefinition.actions.dryrun.includeTriggeredDeliveryDefinitions')"
          />
        </vl-form-column>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving" :mod-loading="isSaving">{{
            $t('deliveryprocessdefinition.actions.dryrun.label')
          }}</vl-button>
          <vl-button :mod-disabled="isSaving" mod-icon-before mod-secondary tag-name="a" @click.prevent="cancel">
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useDeliveryProcessDefinitionDetailStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-detail.store';
import { useDeliveryRunStore } from '@/modules/configuration/store/delivery-run/delivery-run.store';
import { reactive } from 'vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useForm } from 'vee-validate';
import { boolean, object } from 'yup';
import PbsRadioButtonField from '@/components/forms/PbsRadioButtonField.vue';
import { useOperationEntityDataMappingStore } from '@/modules/configuration/store/operation-entity-data-mapping/operation-entity-data-mapping.store';
import { parseException } from '@/modules/core/store/alert.store';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const detailStore = useDeliveryProcessDefinitionDetailStore();
const operationEntityDataMappingStore = useOperationEntityDataMappingStore();
const runStore = useDeliveryRunStore();

const props = defineProps({
  id: {
    type: Number,
    required: true
  }
});

const validationSchema = object({
  includeProductProcessDefinitions: boolean(),
  includeTriggeredDeliveryDefinitions: boolean(),
  importData: boolean().test(
    'has-data-migration',
    t('deliveryprocessdefinition.actions.dryrun.noMigration'),
    (value) => (value ? data.hasDataMigration : true)
  )
});

const { handleSubmit, resetForm } = useForm({ validationSchema });

const data = reactive({
  definition: null,
  hasDataMigration: null,
  isSaving: false,
  isLoading: false,
  error: null
});

onMounted(async () => {
  try {
    data.isLoading = true;
    await loadOperationEntityDataMapping();
    await loadDeliveryProcessDefinition();
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }

  setInitialFormValues();
});

const setInitialFormValues = () => {
  resetForm({
    values: {
      includeProductProcessDefinitions: true,
      includeTriggeredDeliveryDefinitions: true,
      importData: false
    }
  });
};

const loadDeliveryProcessDefinition = async () => {
  const deliveryProcessDefinition = await detailStore.getDetail(props.id);
  if (deliveryProcessDefinition) {
    data.definition = deliveryProcessDefinition;
  }
};

const loadOperationEntityDataMapping = async () => {
  const operationEntityDataMapping = await operationEntityDataMappingStore.getByDefinition(props.id);
  data.hasDataMigration = operationEntityDataMapping?.length > 0;
};

const hasParent = computed(() => {
  return data.definition?.parent?.id;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const error = computed(() => {
  return data.error;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const definition = computed(() => {
  return data.definition;
});

const onSuccess = async (values) => {
  await startdryrun(values.includeProductProcessDefinitions, values.importData, values.includeTriggeredDeliveryDefinitions);

  closeModal();
};

const startdryrun = async (includeProductProcessDefinitions: boolean, importData: boolean, includeTriggeredDeliveryDefinitions: boolean): Promise<void> => {
  try {
    data.isSaving = true;
    await runStore.startdryrun(props.id, includeProductProcessDefinitions, importData, includeTriggeredDeliveryDefinitions );
  } catch (e) {
    console.error(e);
  } finally {
    data.isSaving = false;
  }
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>
<style lang="scss" scoped></style>
